import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, HostBinding, Inject, Input, OnChanges,
  OnDestroy, OnInit, Output, PLATFORM_ID
} from '@angular/core';

import { combineLatest, Observable, of, Subject, Subscription, filter, map, switchMap, take, takeUntil } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';

import { Product, ProductService } from '@spartacus/core';

import { BrandsEnum, DataTableColumnTypeEnum, IComparator, IProductCard, IProductClassification } from '@seb/web-design-system/core';
import { SdProductComparatorComponent } from '@seb/web-design-system/product-comparator';
import { SdProductSellerModalComponent } from '@seb/web-design-system/product-seller';

import { BaseStoreService, CartFacadeService, ImageService, ProductComparatorService, StockService, TrackingService } from '../../../services';
import { ProductNotificationModalComponent } from '../../static/product-notification-modal/product-notification-modal.component';
import { OmnibusRecommendedPriceModalComponent } from '../../static/omnibus-recommended-price-modal/omnibus-recommended-price-modal.component';
import { ComparedProductCard, CustomClassification } from '../../../models';
import { ProductScopeEnum } from '../../../enums';
import { ProductMapper } from '../../../mappers';

@Component({
  selector: 'wc-base-comparator',
  templateUrl: './base-comparator.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdProductComparatorComponent,
    SdProductSellerModalComponent,
    ProductNotificationModalComponent,
    OmnibusRecommendedPriceModalComponent
  ]
})
export class BaseComparatorComponent implements OnInit, OnChanges, OnDestroy {
  @Input() class = '';

  @Input()
  isReadOnly: boolean;

  @Input() comparedProductsCards: ComparedProductCard[];

  @Input() productCodes: string[];

  @Input() isUserFriendly = false;

  @Input() context: string;

  @Input()
  brandCorner = BrandsEnum.default;

  @Input()
  compareOnLoad = false;

  @Input()
  isReadOnlyAccordion = false;

  @Output() hasLoaded = new EventEmitter<void>();

  productsHasBestSeller: number;

  products: IComparator[];
  classifications: IProductClassification[];

  maxProducts: number;

  notifyModalOpened = false;

  isOpenedSellerModal = false;

  notifyProduct = '';
  notifyProductName = '';

  currentProducts: Product[] = [];
  staticComparator$: Subscription;
  pdpComparator$: Subscription;
  isOpenedRecommendedModal = false;
  isNewPriceManagementActivated: boolean;
  isSSR = true;

  private readonly destroy$: Subject<void> = new Subject();

  @HostBinding('class')
  get hostClasses(): string {
    return [this.class, 'wc-component is-base'].filter(Boolean).join(' ');
  }
  constructor(
    private productComparatorService: ProductComparatorService,
    private productService: ProductService,
    private productMapper: ProductMapper,
    private router: Router,
    private readonly cartFacadeService: CartFacadeService,
    private cdRef: ChangeDetectorRef,
    private imageService: ImageService,
    private readonly trackingService: TrackingService,
    private readonly baseStoreService: BaseStoreService,
    private readonly stockService: StockService,
    @Inject(PLATFORM_ID) protected platformId?: any
  ) {}

  ngOnInit(): void {
    this.productComparatorService.maxProducts$
      .pipe(takeUntil(this.destroy$)).subscribe((max) => {
        this.isNewPriceManagementActivated = this.baseStoreService.baseStore.activateNewPriceManagement;
        this.maxProducts = max;
        this.cdRef.markForCheck();
      });
  }

  ngOnChanges(): void {
    this.updateProductInComparator();
  }

  openSellerModal(): void {
    this.isOpenedSellerModal = true;
  }

  addProduct(): void {
    this.router.navigateByUrl(this.comparedProductsCards[0]?.category?.url);
  }

  removeProduct(productCode: string): void {
    this.productComparatorService.removeProduct(productCode);
  }

  removeLastProduct(): void {
    const categoryUrl = this.comparedProductsCards[0]?.category?.url;
    this.productComparatorService.emptyProducts();
    this.router.navigateByUrl(categoryUrl);
  }

  addToCart(productCode: string): void {
    sessionStorage.setItem('addToCartContext', this.context);
    const currentProduct = this.currentProducts.find((product) => product.code === productCode);
    this.cartFacadeService.addToCartShippingType(currentProduct, 1);
  }

  getProducts(products: Product[], productCards: IProductCard[]): IComparator[] {
    return products
      ?.filter((product) => !!product)
      .map((product, index) => ({
        ...productCards[index],
        classifications: this.productMapper.classificationToProductClassification(product.classifications)
      }));
  }

  getHasBestSellerProduct(products: Product[]): number {
    return products.findIndex((product) => product.bestSeller) + 1;
  }

  checkComparatorType(classification: CustomClassification): boolean {
    return this.isUserFriendly ? classification.type === 'USER_FRIENDLY' : classification.type !== 'USER_FRIENDLY';
  }

  getClassification(products: Product[]): IProductClassification[] {
    const classifications: IProductClassification[] = [];
    const productLength = products.length;
    products.forEach((product) => {
      this.currentProducts.push(product);
      product?.classifications
        ?.filter((classification) => !!classification && this.checkComparatorType(classification))
        .forEach((classification: CustomClassification) => {
          const classificationIndex = classifications.findIndex((c) => c.code === classification.code);
          if (classificationIndex === -1) {
            classifications.push({
              ...classification,
              features: classification.features
                ?.filter((f) => productLength === 1 || f.comparable)
                .map((feature) => ({
                  name: feature.name,
                  code: feature.code,
                  description: feature.description,
                  type: DataTableColumnTypeEnum.media,
                  media: this.imageService.normalizeMedia(feature.picto)
                }))
            } as IProductClassification);
          } else {
            classification.features
              ?.filter((f) => productLength === 1 || f.comparable)
              .forEach((feature) => {
                if (classifications[classificationIndex].features.findIndex((f) => f.code === feature.code) === -1) {
                  classifications[classificationIndex].features.push({
                    name: feature.name,
                    code: feature.code,
                    description: feature.description,
                    type: DataTableColumnTypeEnum.media,
                    media: this.imageService.normalizeMedia(feature.picto)
                  });
                }
              });
          }
        });
    });
    return classifications;
  }

  notifyMeProduct(productCode: string): void {
    const currentProduct = this.currentProducts.find((product) => product.code === productCode);
    this.notifyProductName = currentProduct.name;
    this.notifyProduct = productCode;
    this.notifyModalOpened = true;
  }

  updateProductInComparator(): void {
    if (this.comparedProductsCards) {
      this.pdpComparator$?.unsubscribe();
      this.isReadOnly = false;

      this.staticComparator$ = combineLatest(
        this.comparedProductsCards.map((product) => this.productService.get(product.code, ProductScopeEnum.COMPARATOR))
      )
        .pipe(
          filter((products) => !!products && products.every((p) => !!p)),
          switchMap((products) => {
            const productCards$: Observable<IProductCard>[] = products.map((product) =>
              this.productMapper.productToProductCard(product)
            );

            return combineLatest([of(products), ...productCards$]);
          }),
          take(1)
        )
        .subscribe(([products, ...productCards]: [Product[], IProductCard]) => {
          this.classifications = this.getClassification(products);
          this.products = this.getProducts(products, productCards);
          this.hasLoaded.emit();

          this.updateProductPrice();
          this.cdRef.markForCheck();
        });
    }

    if (this.productCodes) {
      this.staticComparator$?.unsubscribe();
      this.isReadOnly = true;

      this.pdpComparator$ = combineLatest(
        this.productCodes.map((code) => this.productService.get(code, ProductScopeEnum.COMPARATOR))
      )
        .pipe(
          filter((products) => !!products && products.every((p) => !!p)),
          switchMap((products) => {
            const productCards$: Observable<IProductCard>[] = products.map((product) =>
              this.productMapper.productToProductCard(product)
            );
            return combineLatest([of(products), ...productCards$]);
          }),
          take(1)
        )
        .subscribe(([products, ...productCards]: [Product[], IProductCard]) => {
          this.classifications = this.getClassification(products);
          this.products = this.getProducts(products, productCards);
          this.productsHasBestSeller = this.getHasBestSellerProduct(products);

          this.updateProductPrice();
          this.hasLoaded.emit();
          this.cdRef.markForCheck();
        });
    }
  }

  updateProductPrice() {
    if (isPlatformBrowser(this.platformId)) {
      const updateProductPrices$: Observable<IComparator>[] = this.products
        .map((product: IComparator) => {
          return this.productService.get(product.code, [ProductScopeEnum.PRICE, ProductScopeEnum.STOCK]).pipe(
            filter((p) => !!p),
            map((productData) => {
              const formattedPrices = this.productMapper.formatCardPrice(productData, this.isNewPriceManagementActivated);
              return {
                ...product,
                price: formattedPrices?.price,
                discountedPrice: formattedPrices?.discountedPrice,
                recommendedPrice: formattedPrices?.recommendedPrice,
                stock: {
                  ...product?.stock,
                  lowStock: this.stockService.checkStock(productData?.price?.value, productData?.stock?.stockLevel)
                }
              };
            })
          );
        });

      combineLatest(updateProductPrices$).pipe(
        take(1)
      ).subscribe((updatedProducts: IComparator[]) => {
        const updatedProductsMap = new Map(updatedProducts.map((product) => [product.code, product]));
        this.products = this.products.map((product) => {
          if (updatedProductsMap.has(product.code)) {
            return { ...product, ...updatedProductsMap.get(product.code) };
          }
          return product;
        });
        this.isSSR = false;
        this.cdRef.markForCheck();
      });
    }
  }

  ngOnDestroy(): void {
    this.staticComparator$?.unsubscribe();
    this.pdpComparator$?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onProductClick(productCodeClicked: string): void {
    this.productService.get(productCodeClicked, ProductScopeEnum.CARD).subscribe((productClicked) => {
      const productEvent = this.trackingService.productToEventProduct(productClicked);
      this.trackingService.sendEvent('productClick', productEvent);
    });
  }

  openRecommendedPriceModal() {
    this.isOpenedRecommendedModal = true;
  }
}
